import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { handleError, handleSuccess } from '../../utils';
import { ToastContainer } from 'react-toastify';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Pagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon
} from '@mui/icons-material';

const Languages = () => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLanguages, setTotalLanguages] = useState(0);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  // Form states
  const [languageName, setLanguageName] = useState('');
  const [editingLanguage, setEditingLanguage] = useState(null);
  const [deletingLanguage, setDeletingLanguage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  const axiosConfig = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  };

  const fetchLanguages = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/languages?page=${page}&limit=10`, axiosConfig);
      const result = response.data;

      if (result.success) {
        setLanguages(result.data);
        setTotalPages(result.pagination.totalPages);
        setCurrentPage(result.pagination.currentPage);
        setTotalLanguages(result.pagination.totalLanguages);
      } else {
        handleError(result.message || 'Failed to fetch languages');
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      handleError('Failed to fetch languages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLanguages();
  }, []);

  // Create Language
  const handleCreate = async () => {
    if (!languageName.trim()) {
      handleError('Language name is required');
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${API_URL}/api/languages`, 
        { name: languageName.trim() }, 
        axiosConfig
      );

      if (response.data.success) {
        handleSuccess('Language created successfully');
        setCreateModalOpen(false);
        setLanguageName('');
        fetchLanguages(currentPage);
      } else {
        handleError(response.data.message || 'Failed to create language');
      }
    } catch (error) {
      console.error('Error creating language:', error);
      if (error.response?.data?.message) {
        handleError(error.response.data.message);
      } else {
        handleError('Failed to create language');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Edit Language
  const handleEditClick = (language) => {
    setEditingLanguage(language);
    setLanguageName(language.name);
    setEditModalOpen(true);
  };

  const handleEdit = async () => {
    if (!languageName.trim()) {
      handleError('Language name is required');
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.put(`${API_URL}/api/languages/${editingLanguage._id}`, 
        { name: languageName.trim() }, 
        axiosConfig
      );

      if (response.data.success) {
        handleSuccess('Language updated successfully');
        setEditModalOpen(false);
        setLanguageName('');
        setEditingLanguage(null);
        fetchLanguages(currentPage);
      } else {
        handleError(response.data.message || 'Failed to update language');
      }
    } catch (error) {
      console.error('Error updating language:', error);
      if (error.response?.data?.message) {
        handleError(error.response.data.message);
      } else {
        handleError('Failed to update language');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Language
  const handleDeleteClick = (language) => {
    setDeletingLanguage(language);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      const response = await axios.delete(`${API_URL}/api/languages/${deletingLanguage._id}`, axiosConfig);

      if (response.data.success) {
        handleSuccess('Language deleted successfully');
        setDeleteModalOpen(false);
        setDeletingLanguage(null);
        fetchLanguages(currentPage);
      } else {
        handleError(response.data.message || 'Failed to delete language');
      }
    } catch (error) {
      console.error('Error deleting language:', error);
      if (error.response?.data?.message) {
        handleError(error.response.data.message);
      } else {
        handleError('Failed to delete language');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Close modals
  const closeModals = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setDeleteModalOpen(false);
    setLanguageName('');
    setEditingLanguage(null);
    setDeletingLanguage(null);
  };

  const handlePageChange = (event, page) => {
    fetchLanguages(page);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          ml: '80px',
          p: 2,
          backgroundColor: '#ffffff',
          minHeight: '100vh'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" color="text.secondary">
            Loading languages...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        ml: '80px',
        p: 2,
        minHeight: '100vh',
        backgroundColor: '#ffffff'
      }}
    >
      <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                mb: 0.5,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              Languages Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalLanguages} languages found
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            ADD LANGUAGE
          </Button>
        </Box>

        {/* Languages Table */}
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LanguageIcon color="primary" />
                    Language Name
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Created Date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {languages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                    <LanguageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No languages found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Get started by adding your first language
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setCreateModalOpen(true)}
                    >
                      Add Language
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                languages.map((language) => (
                  <TableRow 
                    key={language._id}
                    sx={{ 
                      '&:hover': { backgroundColor: '#f9f9f9' },
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}
                  >
                    <TableCell sx={{ fontSize: '1rem', fontWeight: 500 }}>
                      {language.name}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {formatDate(language.createdAt)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleEditClick(language)}
                        size="small"
                       sx={{
      mr: 1,
      color: 'white', // Changed to white
      backgroundColor: 'primary.main', // Changed to solid background
      '&:hover': {
        backgroundColor: 'primary.dark',
        color: 'white',
        transform: 'scale(1.1)'
      },
      transition: 'all 0.2s ease'
    }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(language)}
                        size="small"
                       sx={{
      color: 'white', // Changed to white
      backgroundColor: 'error.main', // Changed to solid background
      '&:hover': {
        backgroundColor: 'error.dark',
        color: 'white',
        transform: 'scale(1.1)'
      },
      transition: 'all 0.2s ease'
    }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Create Modal */}
        <Dialog open={createModalOpen} onClose={closeModals} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
            Add New Language
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Language Name"
              fullWidth
              variant="outlined"
              value={languageName}
              onChange={(e) => setLanguageName(e.target.value)}
              placeholder="e.g., Portuguese, Spanish, English"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button onClick={closeModals} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleCreate} 
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {submitting ? 'Creating...' : 'Create Language'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={editModalOpen} onClose={closeModals} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
            Edit Language
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Language Name"
              fullWidth
              variant="outlined"
              value={languageName}
              onChange={(e) => setLanguageName(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button onClick={closeModals} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleEdit} 
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <EditIcon />}
            >
              {submitting ? 'Updating...' : 'Update Language'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModalOpen} onClose={closeModals} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
            Delete Language
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{deletingLanguage?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button onClick={closeModals} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              variant="contained"
              color="error"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {submitting ? 'Deleting...' : 'Delete Language'}
            </Button>
          </DialogActions>
        </Dialog>

        <ToastContainer />
      </Box>
    </Box>
  );
};

export default Languages;
